import React from "react";
import { Button } from "../../ui/button";
import { Progress } from "../../ui/progress";
import { EditIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import PaginatedTable, { PaginatedTableColumn } from "../../ui/paginated-table";
import {
  SelectedDataset,
  calculateSampledRequestCount
} from "./CompositionDetail";

interface CompositionReviewTableProps {
  selectedDatasets: SelectedDataset[];
  onConfigureDataset: (dataset: SelectedDataset) => void;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}

const CompositionReviewTable: React.FC<CompositionReviewTableProps> = ({
  selectedDatasets,
  onConfigureDataset,
  loading = false,
  emptyTitle = "No datasets selected",
  emptyDescription = "Add datasets to your composition to see them here."
}) => {
  const getSamplingTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "request":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "session":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  // Transform SelectedDataset to table data format
  const tableData = selectedDatasets
    .map(item => ({
      key: item.dataset.id,
      name: item.dataset.name,
      description: item.dataset.description,
      originalRequests: item.dataset.request_count,
      sampledRequests: calculateSampledRequestCount(item.dataset, item.samplePercentage),
      sampleType: item.sampleType,
      samplePercentage: item.samplePercentage,
      percentage: item.percentage,
      isSession: item.sampleType === "session",
      original: item
    }))
    .sort((a, b) => b.percentage - a.percentage);

  const columns: PaginatedTableColumn<typeof tableData[0]>[] = [
    {
      key: "name",
      header: "Dataset",
      width: "w-[35%]",
      render: (record) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-med text-foreground">{record.name}</div>
          <div className="text-xs text-muted-foreground line-clamp-1">
            {record.description || "No description"}
          </div>
        </div>
      )
    },
    {
      key: "originalRequests",
      header: "Requests",
      width: "w-[25%]",
      render: (record) => (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">
            Original: <span className="font-gc-med text-foreground">{record.originalRequests.toLocaleString()}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Sampled: <span className="font-gc-med text-blue-600">
              {record.sampledRequests.toLocaleString()}{record.isSession ? "*" : ""}
            </span>
          </div>
        </div>
      )
    },
    {
      key: "sampleType",
      header: "Sampling",
      width: "w-[20%]",
      render: (record) => (
        <div className="space-y-1">
          <span className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            getSamplingTypeColor(record.sampleType)
          )}>
            {record.sampleType}
          </span>
          <div className="text-xs text-muted-foreground">
            {record.samplePercentage}% sampled
          </div>
        </div>
      )
    },
    {
      key: "percentage",
      header: "Distribution",
      width: "w-[15%]",
      render: (record) => (
        <div className="w-full">
          <Progress 
            value={record.percentage} 
            className="h-2"
          />
          <div className="text-xs text-center mt-1 font-gc-med text-foreground">
            {record.percentage}%
          </div>
        </div>
      )
    },
    {
      key: "key",
      header: "Actions",
      width: "w-[5%]",
      render: (record) => (
        <Button 
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onConfigureDataset(record.original);
          }}
          className="h-8 w-8 p-0"
        >
          <EditIcon className="w-3 h-3" />
        </Button>
      )
    }
  ];

  return (
    <PaginatedTable
      data={tableData}
      columns={columns}
      loading={loading}
      currentPage={1}
      onPageChange={() => {}} // No pagination needed for this table
      pageSize={tableData.length}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      showPagination={false}
      loadingRows={3}
    />
  );
};

export default CompositionReviewTable; 